// import hooks
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

// import types
import { TabProps } from '../CLASettings.types';

// import components
import Grid from '@mui/material/Grid';
import {
  ControlledCheckbox,
  ControlledTextField
} from 'components/shared/ControlledFields';
import withReadModeField from 'components/shared/CRUD/components/InnerPage/components/withReadModeField';
import ControlledNumericField from 'components/shared/ControlledFields/ControlledNumericField';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';

// import utils
import { leaveSchema } from '../CLASettings.schema';
import { fixedCheckboxLabels, fixedLeaveTransfer } from '../CLASettings.consts';

const ControlledTextFieldWithReadMode = withReadModeField(ControlledTextField);

const ControlledNumericFieldWithReadMode = withReadModeField(
  ControlledNumericField
);

const LeaveTab = ({ isEditable, setCurrentSchema }: TabProps) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'adminPortal.cla'
  });

  useEffect(() => {
    setCurrentSchema(leaveSchema);
  }, []);

  const { control, setValue } = useFormContext();

  return (
    <>
      <MultiStepForm.Section title={t('BalanceBudget')}>
        <Grid
          container
          spacing={{ compact: 2, medium: 3 }}
          columns={{ compact: 4, medium: 8, large: 12 }}
        >
          <Grid
            sx={{
              compact: 2,
              medium: 4,
              expanded: 4
            }}
          >
            <ControlledTextFieldWithReadMode
              disabled={!isEditable}
              control={control}
              variant="outlined"
              name="balanceBudgetName"
              label={t('BalanceBudgetName')}
            />
          </Grid>
          <Grid
            sx={{
              compact: 2,
              medium: 4,
              expanded: 4
            }}
          >
            <ControlledNumericFieldWithReadMode
              disabled={!isEditable}
              variant="outlined"
              name={'balanceBudgetMaxGiftHours'}
              label={t('BalanceBudgetMaxGiftHours')}
            />
          </Grid>
        </Grid>
      </MultiStepForm.Section>
      <MultiStepForm.Section title={t('leaveBuildUp')}>
        <Grid
          container
          spacing={{ compact: 2, medium: 3 }}
          columns={{ compact: 4, medium: 8, large: 12 }}
        >
          {fixedCheckboxLabels.map((val, id) => (
            <Grid
              key={id}
              sx={{
                compact: 2,
                medium: 4,
                expanded: 4
              }}
            >
              <ControlledCheckbox
                key={`leaveBuildUp_${id}`}
                disabled={!isEditable}
                onChange={(event) => {
                  if (val.isInteger) {
                    setValue(val.flag, event.target.checked ? 1 : 0);
                  }
                }}
                control={control}
                name={val.flag}
                label={t(val.label)}
              />
            </Grid>
          ))}
          <Grid
            sx={{
              compact: 2,
              medium: 4,
              expanded: 4
            }}
          >
            <ControlledNumericFieldWithReadMode
              disabled={!isEditable}
              variant="outlined"
              name={'startBeforeDayToReceiveFullMonthOfLeave'}
              label={t('startBeforeDayToReceiveFullMonthOfLeave')}
            />
          </Grid>
          <Grid
            sx={{
              compact: 2,
              medium: 4,
              expanded: 4
            }}
          >
            <ControlledNumericFieldWithReadMode
              disabled={!isEditable}
              variant="outlined"
              name={'endAfterDayToReceiveFullMonthOfLeave'}
              label={t('endAfterDayToReceiveFullMonthOfLeave')}
            />
          </Grid>
        </Grid>
      </MultiStepForm.Section>
      <MultiStepForm.Section title={t('leaveTransfer')}>
        <Grid
          container
          spacing={{ compact: 2, medium: 3 }}
          columns={{ compact: 4, medium: 8, large: 12 }}
        >
          {fixedLeaveTransfer.map((val, id) => (
            <Grid
              key={id}
              sx={{
                compact: 2,
                medium: 4,
                expanded: 4
              }}
            >
              <ControlledCheckbox
                key={`leaveTransfer_${id}`}
                disabled={!isEditable}
                control={control}
                name={val.flag}
                label={t(val.label)}
                onChange={(event) => {
                  if (val.isInteger) {
                    setValue(val.flag, event.target.checked ? 1 : 0);
                  }
                }}
              />
            </Grid>
          ))}
        </Grid>
      </MultiStepForm.Section>
      <MultiStepForm.Section title={t('holidayHours')}>
        <Grid
          container
          spacing={{ compact: 2, medium: 3 }}
          columns={{ compact: 4, medium: 8, large: 12 }}
        >
          <Grid
            sx={{
              compact: 2,
              medium: 4,
              expanded: 4
            }}
          >
            <ControlledCheckbox
              disabled={!isEditable}
              control={control}
              name={'blockFestiveDaysHours'}
              label={t('blockNormalHoliday')}
              onChange={(event) => {
                setValue('blockFestiveDaysHours', event.target.checked ? 1 : 0);
              }}
            />
          </Grid>
          <Grid
            sx={{
              compact: 2,
              medium: 4,
              expanded: 4
            }}
          >
            <ControlledCheckbox
              disabled={!isEditable}
              control={control}
              name={'flags.COMP_LEAVE_EXACT_HOLIDAYS'}
              label={t('compensationBasedOnHoliday')}
            />
          </Grid>
          <Grid
            sx={{
              compact: 2,
              medium: 4,
              expanded: 4
            }}
          >
            <ControlledNumericFieldWithReadMode
              disabled={!isEditable}
              variant="outlined"
              name={'hoursJusFestDays'}
              label={t('holidayHoursSubtracked')}
            />
          </Grid>
          <Grid
            sx={{
              compact: 2,
              medium: 4,
              expanded: 4
            }}
          >
            <ControlledCheckbox
              disabled={!isEditable}
              control={control}
              name={'flags.JUS_URC'}
              label={t('HTC')}
            />
          </Grid>
          <Grid
            sx={{
              compact: 2,
              medium: 4,
              expanded: 4
            }}
          >
            <ControlledCheckbox
              disabled={!isEditable}
              control={control}
              name={'flags.JUS_MIN_MAX'}
              label={t('minMax')}
            />
          </Grid>
        </Grid>
      </MultiStepForm.Section>
    </>
  );
};

export default LeaveTab;
